// Internal
import { ApiResponse, getRequest, patchRequest, postRequest } from './request';
import {
  AddTxnGroupDto,
  BkV2TabCounts,
  BulkUpdateResponseTransactionGroupDto,
  GetTransactionGroupsDto,
  TransactionDto,
  TransactionGroupDto,
  TxnGroupsCount,
  UngroupTxnGroupDto,
  UpdateTxnGroupDto,
  UpdateTxnGroupSubTxnDto,
  UpdateTxnGroupsDto,
} from './__types/transaction-groups.types';

// react-query
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// MISC
import { get, omit } from 'lodash-es';
import { processTxnGroup } from '@/utils/transaction-groups';

// Export types
export * from './__types/transaction-groups.types';

// Query keys
export const txnGroupQueryKeys = {
  all: ['txn-groups'] as const,
  lists: () => [...txnGroupQueryKeys.all, 'list'] as const,
  list: (dto: GetTransactionGroupsDto) => [...txnGroupQueryKeys.lists(), dto] as const,
  countLists: () => [...txnGroupQueryKeys.all, 'count-list'] as const,
  countList: (dto: GetTransactionGroupsDto) => [...txnGroupQueryKeys.countLists(), dto] as const,
  errorCountLists: () => [...txnGroupQueryKeys.all, 'error-count-list'] as const,
  errorCountList: (dto: GetTransactionGroupsDto) =>
    [...txnGroupQueryKeys.errorCountLists(), dto] as const,
  tabCountLists: () => [...txnGroupQueryKeys.all, 'tab-count-list'] as const,
  tabCountList: (dto: GetTransactionGroupsDto) =>
    [...txnGroupQueryKeys.tabCountLists(), dto] as const,
};

export const getTransactionGroups = (dto: GetTransactionGroupsDto, abortSignal?: AbortSignal) => {
  const params = generateBookkeepingV2RequestParams(dto);

  return getRequest<ApiResponse<TransactionGroupDto[]>>(
    `/transaction-groups?${params.toString()}`,
    null,
    {},
    {
      // Cancellable request
      signal: abortSignal,
    },
  );
};
export const useGetTransactionGroups = (dto: GetTransactionGroupsDto) => {
  return useQuery<TransactionGroupDto[], Error>({
    queryKey: txnGroupQueryKeys.list(dto),
    queryFn: async () => {
      const { body } = await getTransactionGroups(dto);
      return body.data;
    },
  });
};

const _createTxnGroup = (dto: AddTxnGroupDto) =>
  postRequest<ApiResponse<TransactionGroupDto>>('/transaction-groups', dto);
export const useCreateTxnGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: AddTxnGroupDto) => {
      const { body } = await _createTxnGroup(dto);

      return body.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(txnGroupQueryKeys.lists());
    },
    onError: () => {
      // Skips the app's default react-query error handling
      return false;
    },
  });
};

export const getTransactionGroupsCount = (dto: GetTransactionGroupsDto) => {
  const params = generateBookkeepingV2RequestParams(dto);

  return getRequest<ApiResponse<TxnGroupsCount>>(
    `/transaction-groups/count?${params.toString()}`,
    null,
  );
};
export const useGetTransactionGroupsCount = (dto: GetTransactionGroupsDto) => {
  return useQuery<TxnGroupsCount, Error>({
    queryKey: txnGroupQueryKeys.countList(dto),
    queryFn: async () => {
      const { body } = await getTransactionGroupsCount(dto);
      return body.data;
    },
  });
};

const _getBookkeepingV2TabCounts = (dto: GetTransactionGroupsDto) => {
  const params = generateBookkeepingV2RequestParams(dto);

  return getRequest<ApiResponse<BkV2TabCounts>>(
    `/transaction-groups/count-for-tabs?${params.toString()}`,
    null,
  );
};
export const useGetBookkeepingV2TabCounts = (dto: GetTransactionGroupsDto) => {
  return useQuery<BkV2TabCounts, Error>({
    queryKey: txnGroupQueryKeys.tabCountList(dto),
    queryFn: async () => {
      const { body } = await _getBookkeepingV2TabCounts(dto);
      return body.data;
    },
  });
};

export const getTransactionGroupsErrorCount = (
  dto: GetTransactionGroupsDto,
  abortSignal?: AbortSignal,
) => {
  const params = generateBookkeepingV2RequestParams(dto);

  return getRequest<ApiResponse<number>>(
    `/transaction-groups/errors-count?${params.toString()}`,
    null,
    {},
    {
      // Cancellable request
      signal: abortSignal,
    },
  );
};

const _ungroupTxnGroup = (dto: UngroupTxnGroupDto) =>
  patchRequest<ApiResponse<TransactionGroupDto>>(`/transaction-groups/${dto.id}/ungroup`, {});
export const useUngroupTxnGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: UngroupTxnGroupDto) => {
      const { body } = await _ungroupTxnGroup(dto);

      return body.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(txnGroupQueryKeys.lists());
    },
  });
};

const _updateTxnGroup = (dto: UpdateTxnGroupDto) =>
  patchRequest<ApiResponse<TransactionGroupDto>>(`/transaction-groups/${dto.id}`, omit(dto, 'id'));
export const useUpdateTxnGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: UpdateTxnGroupDto) => {
      const { body } = await _updateTxnGroup(dto);

      return body.data;
    },
    onSuccess: (_newGroupData: TransactionGroupDto) => {
      queryClient
        .getQueryCache()
        .findAll(txnGroupQueryKeys.lists())
        .forEach(({ queryKey }) => {
          queryClient.setQueryData(queryKey, (oldData?: TransactionGroupDto[]) => {
            return oldData?.map((group) => {
              if (group.id === _newGroupData.id) {
                return processTxnGroup(_newGroupData);
              }

              return group;
            });
          });
        });
    },
  });
};

const _updateTxnGroups = (dto: UpdateTxnGroupsDto) => {
  const params = generateBookkeepingV2RequestParams(dto.params);

  return patchRequest<ApiResponse<TransactionGroupDto>>(
    `/transaction-groups?${params.toString()}`,
    {
      ...{ ...dto.selectionState, toggleNodes: dto.selectionState?.toggledNodes },
      ...dto.updates,
    },
  );
};
export const useUpdateTxnGroups = () => {
  const queryClient = useQueryClient();

  return useMutation<BulkUpdateResponseTransactionGroupDto, unknown, UpdateTxnGroupsDto>({
    mutationFn: async (dto) => {
      const { body } = await _updateTxnGroups(dto);

      return body.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(txnGroupQueryKeys.lists());
    },
  });
};

const _updateTxnGroupSubTxn = (dto: UpdateTxnGroupSubTxnDto) =>
  patchRequest<ApiResponse<TransactionDto>>(
    `/transaction-groups/${dto.txnGroupId}/transactions/${dto.txnId}`,
    omit(dto, ['txnGroupId', 'txnId']),
  );
export const useUpdateTxnGroupSubTxn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: UpdateTxnGroupSubTxnDto) => {
      const { body } = await _updateTxnGroupSubTxn(dto);

      return body.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(txnGroupQueryKeys.lists());
    },
  });
};

const _stopTxnGroupUpdateByQuery = () => {
  return postRequest<ApiResponse<null>>('/transaction-groups/stop-update-by-query', {});
};
export const useStopTxnGroupUpdateByQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { body } = await _stopTxnGroupUpdateByQuery();

      return body.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(txnGroupQueryKeys.all);
    },
  });
};

// Generates a URLSearchParams object from the dto
export const generateBookkeepingV2RequestParams = (dto: GetTransactionGroupsDto) => {
  const params = new URLSearchParams(dto?.filtersQueryString || '');
  ['limit', 'page', 'tabSelection'].forEach((key) => {
    const _value = get(dto, key);
    if (_value !== undefined && _value !== null) {
      params.append(key, `${_value}`);
    }
  });

  if (dto?.sortByQueryString) {
    params.append('sortBy', dto.sortByQueryString);
  }

  return params;
};
